import React, { MouseEvent, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import Tag from "@/components/common/Tag/Tag";
import { RightArrowIcon } from "@/components/icons/RightArrowIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { Child, HeaderDataItem } from "@/data/navigation/header.data";
import { useNavigation } from "@/hooks/useNavigation";
import { NavBanner, PromoData } from "@/types/promoData";
import { GA4 } from "@/utils/ga4Utils";
import { cn, isIpad } from "@/utils/general";

import styles from "./NavigationShop.module.scss";
import PromoSaleBanner from "./PromoSaleBanner";
import StrandSerumNavBanner from "./StrandSerumNavBanner";

// Import HeaderDataItem and Child directly for type consistency

// Props for NavigationShopMenu: now correctly expects a HeaderDataItem
interface NavigationShopMenuProps {
  item: HeaderDataItem;
  promoCf: PromoData;
}

const removeActiveItemClass = (e: MouseEvent<HTMLElement>, preventClick?: boolean) => {
  const mouseLeftItem = hasHover(e);
  if (mouseLeftItem) {
    mouseLeftItem.classList.remove("hover");
  }
  if (preventClick) {
    e.preventDefault();
  }
  handleExpand(e.currentTarget);
};

const handleExpand = (el: HTMLElement) => {
  if (el?.id) {
    document.querySelectorAll("nav.navigation-variant [aria-expanded]").forEach((a) => {
      const isHover = a?.parentElement?.classList.contains("hover");
      a.setAttribute("aria-expanded", isHover ? "true" : "false");
    });
  }
};

const hasHover = (e: MouseEvent<HTMLElement>): HTMLElement | null => {
  return (e.target as HTMLElement)?.closest(".has-subnav.hover");
};

// `subitem` is now explicitly typed as `Child`
const buildCls = (subitem: Child): string => {
  return subitem.subtext ? "has-subtext" : "no-subtext";
};

const trackClicked = (text: string, destination: string) => {
  GA4.onNavigationClicked({
    type: "button",
    text,
    destination,
  });
};

const NavigationShopMenu: React.FC<NavigationShopMenuProps> = ({ item, promoCf }) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigation();

  const onTouchStart = (url: string) => {
    if (isIpad()) {
      navigate(url);
    }
  };

  const promoNavData: NavBanner = promoCf?.savingsData?.web?.nav?.women;

  return (
    <div
      className={cn(
        styles.dropdownMenu,
        "dropdown dropdown-shop-desktop",
        "w-[min(calc(100dvw-63px),1321px)]"
      )}
    >
      <div className={`${styles.dropdownWrapper} row`}>
        <div className={`${styles.mainBox} box-left`}>
          {item.children && (
            <div className="flex flex-nowrap justify-between items-stretch">
              <div className={`${styles.shopItems} shop-items`}>
                <div className={styles.shopItemsTitle}>
                  <TextElementNova
                    element="h3"
                    text={item.childrenTitle}
                    className="Subtitle-10-Dia-Normal"
                  />
                </div>

                <div className={styles.shopItemsList}>
                  {item.children.map((subitem, idx) => (
                    <div
                      key={`nav-item-${idx}`}
                      className={`${styles.navItemContainer} ${subitem.name}`}
                    >
                      <div
                        onTouchStart={() => onTouchStart(subitem.url)}
                        className={`${styles.navItem} ${item.name}-nav-item`}
                      >
                        <LinkHandler
                          path={subitem.url}
                          onClick={() => trackClicked(subitem.name, subitem.url)}
                        >
                          <div
                            onMouseEnter={() => setHoveredItem(subitem.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                            onClick={(e) => removeActiveItemClass(e)}
                            className={styles.subItemWrapper}
                          >
                            <div
                              className={`${styles.subItem} ${subitem.name.toLowerCase()} ${buildCls(
                                subitem
                              )} group ${hoveredItem === subitem.name ? styles.hovered : ""}`}
                            >
                              <div className={styles.subItemContent}>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <TextElementNova
                                      element="h4"
                                      text={subitem.name}
                                      className="Body-20-Dia-Strong text-nova-brand-primary-100"
                                    />
                                    {subitem.tag ? (
                                      <Tag
                                        data={{
                                          line: subitem.tag,
                                          classes: "bg-nova-brand-accent-80",
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                  <TextElementNova
                                    element="p"
                                    text={subitem.subtextDesktop ?? ""}
                                    className="Body-10-Dia-Normal text-nova-base-black mt-2"
                                  />
                                </div>
                                <RightArrowIcon />
                              </div>
                            </div>
                          </div>
                        </LinkHandler>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {item.subCategories && (
                <div className="shop-sub-items pt-8 flex-1">
                  <div className={styles.flexColumn}>
                    <div className={styles.shopSubItemsTitle}>
                      <TextElementNova
                        element="h3"
                        text={item.subCategoriesTitle}
                        className="Body-30-Dia-Normal"
                      />
                    </div>
                    {item.subCategories.map((subCategory, idx) => (
                      <div key={`sub-nav-item-${idx}`}>
                        <div
                          onTouchStart={() => onTouchStart(subCategory.url)}
                          className={`${styles.subNavItem} sub-${item.name}-nav-item`}
                        >
                          <LinkHandler
                            path={subCategory.url}
                            onClick={() => trackClicked(subCategory.name, subCategory.url)}
                          >
                            <div
                              onMouseEnter={() => setHoveredItem(subCategory.name)}
                              onMouseLeave={() => setHoveredItem(null)}
                              onClick={(e) => removeActiveItemClass(e)}
                              className={styles.subItemWrapper}
                            >
                              <div
                                className={`${styles.subItem} ${subCategory.name.toLowerCase()} ${buildCls(
                                  subCategory
                                )}`}
                              >
                                <div className={styles.subItemContent}>
                                  <div className="flex items-center gap-2">
                                    <TextElementNova
                                      element="h4"
                                      text={subCategory.name}
                                      className="Body-20-Dia-Strong"
                                    />
                                    {subCategory.tag ? (
                                      <Tag
                                        data={{
                                          line: subCategory.tag,
                                          classes: "bg-nova-brand-accent-80",
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                  <RightArrowIcon />
                                </div>
                              </div>
                            </div>
                          </LinkHandler>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {promoNavData ? (
                <PromoSaleBanner data={promoNavData} />
              ) : (
                <StrandSerumNavBanner className="ml-4 lg:max-w-[279px] min-[1184px]:ml-16 min-[1184px]:max-w-[356px]" />
              )}
            </div>
          )}

          <div className={styles.buttonSection}>
            <div className={styles.buttonWrapper}>
              <ButtonNova
                width="100%"
                icon="Right"
                label="Shop All Products"
                className="desktop-button"
                onClick={() => {
                  trackClicked("Shop All Products", "/hair/brand/product/category/");
                  navigate("/hair/brand/product/category/");
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationShopMenu;
